import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { BehaviorSubject, Subject, take, takeUntil } from 'rxjs';
import { randomId } from 'server/shared/helpers/random';
import { trimPhone } from 'server/shared/helpers/trimPhoneHelper';
import {
  Customer,
  JobRequestApprovalNotificationSettings,
  JobRequestDaySchedule,
  JobRequestTimeRange,
  JobRequestWeekdayScheduleSettings,
} from 'server/shared/models/customer';
import { Department } from 'server/shared/models/department';
import { getObjectKeys } from 'server/shared/models/getObjectKeys';
import { WeekDayEnum } from 'server/shared/models/weekday.enum';
import { rxNgZoneRun } from 'src/helpers/rxNgZoneRun';
import { CustomerService } from '../../services/customer.service';
import { PermissionsService } from '../../services/permissions.service';
import { ToastService } from '../../services/toast/toast.service';
@Component({
  selector: 'admin-job-request-approval',
  templateUrl: 'job-request-approval.component.html',
  styleUrls: ['job-request-approval.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobRequestApprovalComponent implements OnInit, OnDestroy {
  @ViewChild('notificationForm') notificationForm!: NgForm;

  destroyed$ = new Subject<void>();
  customer$ = new BehaviorSubject<Customer | undefined>(undefined);
  notificationSettings$ = new BehaviorSubject<
    JobRequestApprovalNotificationSettings[]
  >([]);
  weekdayEnum = WeekDayEnum;
  weekdays = Object.values(WeekDayEnum);

  loading$ = new BehaviorSubject<boolean>(true);

  constructor(
    private ngZone: NgZone,
    private toastService: ToastService,
    private customerService: CustomerService,
    private permissionService: PermissionsService,
    private location: Location,
  ) {
    this.permissionService.isCustomerAdmin$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isCustomerAdmin) => {
        if (!isCustomerAdmin) {
          this.historyBack();
        }
      });
  }

  ngOnInit(): void {
    this.customerService.readyDispoCustomer$
      .pipe(take(1), takeUntil(this.destroyed$), rxNgZoneRun(this.ngZone))
      .subscribe((customer) => {
        this.customer$.next(customer);
        if (customer?.jobRequestApprovalNotificationSettings) {
          this.notificationSettings$.next([
            ...customer.jobRequestApprovalNotificationSettings,
          ]);
        }
        this.loading$.next(false);
      });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  addNotificationSetting() {
    const newSetting: JobRequestApprovalNotificationSettings = {
      id: randomId(),
      label: '',
      notificationType: 'email',
      target: '',
      weekdaySchedule: this.createDefaultWeekdaySchedule(),
    };
    const currentSettings = this.notificationSettings$.value;
    currentSettings.push(newSetting);
    this.notificationSettings$.next([...currentSettings]);
  }

  removeNotificationSetting(index: number) {
    const currentSettings = this.notificationSettings$.value;
    currentSettings.splice(index, 1);
    this.notificationSettings$.next([...currentSettings]);
  }

  onDepartmentsChanged(
    departments: Department[] | undefined,
    setting: JobRequestApprovalNotificationSettings,
  ) {
    if (departments) {
      setting.departmentIds = departments.map((dept) => dept.id);
    } else {
      setting.departmentIds = undefined;
    }
  }

  onNotificationTypeChange(
    setting: JobRequestApprovalNotificationSettings,
    targetInput: HTMLInputElement,
  ): void {
    // Clear the target input when notification type changes to avoid validation conflicts
    setting.target = '';

    // Focus the target input after a short delay to allow DOM update
    setTimeout(() => {
      if (targetInput) {
        targetInput.focus();
      }
    }, 50);
  }

  trimPhoneTarget(setting: JobRequestApprovalNotificationSettings): void {
    if (setting.notificationType === 'sms') {
      setting.target = trimPhone(setting.target);
    }
  }

  sortTimeRanges(setting: JobRequestApprovalNotificationSettings) {
    getObjectKeys(setting.weekdaySchedule).forEach((key) => {
      setting.weekdaySchedule[key].timeRanges.sort((a, b) => {
        // If either timeFrom is undefined or empty, do not change order
        if (!a.timeFrom || !b.timeFrom) {
          return 0;
        }
        return a.timeFrom.localeCompare(b.timeFrom);
      });
    });
  }

  saveNotificationSettings() {
    const customer = this.customer$.value;
    if (!customer) return;
    const notificationSettings = this.notificationSettings$.value;
    notificationSettings.forEach((setting) => this.sortTimeRanges(setting));
    // Check form validity
    if (this.notificationForm && !this.notificationForm.valid) {
      this.notificationForm.form.markAllAsTouched();
      this.toastService.add({
        type: 'INFO',
        message: 'InvalidForm',
      });
      return;
    }

    Meteor.call(
      'updateCustomerJobRequestApprovalNotificationSettings',
      notificationSettings,
      (error: any) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: 'serverError',
          });
        } else {
          this.toastService.add({
            type: 'SUCCESS',
            message: 'changesSaved',
          });
        }
      },
    );
  }

  private createDefaultWeekdaySchedule(): JobRequestWeekdayScheduleSettings {
    const defaultDay: JobRequestDaySchedule = {
      enabled: false,
      timeRanges: [],
    };
    return {
      [WeekDayEnum.MONDAY]: { ...defaultDay, timeRanges: [] },
      [WeekDayEnum.TUESDAY]: { ...defaultDay, timeRanges: [] },
      [WeekDayEnum.WEDNESDAY]: { ...defaultDay, timeRanges: [] },
      [WeekDayEnum.THURSDAY]: { ...defaultDay, timeRanges: [] },
      [WeekDayEnum.FRIDAY]: { ...defaultDay, timeRanges: [] },
      [WeekDayEnum.SATURDAY]: { ...defaultDay, timeRanges: [] },
      [WeekDayEnum.SUNDAY]: { ...defaultDay, timeRanges: [] },
    };
  }

  addTimeRange(daySchedule: JobRequestDaySchedule): void {
    const newTimeRange: JobRequestTimeRange = {
      id: randomId(),
      timeFrom: '',
      timeTo: '',
    };
    daySchedule.timeRanges.push(newTimeRange);
  }

  removeTimeRange(
    setting: JobRequestApprovalNotificationSettings,
    weekday: WeekDayEnum,
    timeRangeId: string,
  ): void {
    const timeRanges = setting.weekdaySchedule[weekday].timeRanges;
    const index = timeRanges.findIndex((range) => range.id === timeRangeId);
    if (index > -1) {
      timeRanges.splice(index, 1);
    }
  }

  historyBack() {
    this.location.back();
  }
}
